import React, { useEffect, useRef, useState } from 'react';
import { loadStripeJs } from './stripe';
import { stripeStart, stripeConfirm } from './auth';
import { walletLabel } from './PaymentMethods';

const cfg = (typeof window !== 'undefined' && window.CHECKOUT_NEXT) || {};

// Wallet payment via Stripe, device-aware:
//   • Android (Google Pay) → the Payment Request API + paymentRequestButton. Stripe's
//     newer Express Checkout Element does NOT reliably surface Google Pay for this
//     account on Android (verified: the classic paymentRequest button does), so we use
//     the proven API there.
//   • iOS / desktop (Apple Pay) → the Express Checkout Element, which uniquely provides
//     Apple Pay's cross-device QR on non-Safari desktop.
// Both paths share the server flow: cn_stripe_start builds the order + PaymentIntent,
// we confirm, and a parallel poll (webhook/fallback) races the redirect to the thank-you.
export default function WalletPay({ amount, currency = 'eur', buildPayload }) {
  const nodeRef = useRef(null);
  const [status, setStatus] = useState('loading'); // loading | ready | unavailable | error
  const [msg, setMsg] = useState('');
  const wl = walletLabel();
  const label = wl.title || 'Apple Pay / Google Pay';
  const useProductRequest = wl.brand === 'gpay'; // Android → Payment Request (Google Pay)

  useEffect(() => {
    if (!cfg.stripePk || !amount) { setStatus('unavailable'); return undefined; }
    let cancelled = false;
    let element = null;

    // Diagnostics — record wallet availability from real sessions instead of guessing.
    const diag = (props) => {
      try { if (window.posthog && window.posthog.capture) window.posthog.capture('wallet_availability', { brand: wl.brand, amount, ...props }); } catch (e) { /* noop */ }
      try { if (window.console) window.console.warn('[WalletPay]', props); } catch (e) { /* noop */ }
    };

    loadStripeJs().then((Stripe) => {
      if (cancelled || !nodeRef.current) return;
      const stripe = Stripe(cfg.stripePk);

      // Shared: create the order + PaymentIntent server-side, then wire the parallel
      // poll + a single idempotent redirect. Returns { data, go, stop } or { err }.
      const beginServerPayment = async (walletType) => {
        const { ok, data } = await stripeStart({ ...buildPayload(), walletType });
        if (!ok || !data.clientSecret) { return { err: (data && data.message) || 'Δεν ήταν δυνατή η έναρξη της πληρωμής.' }; }
        const state = { redirected: false };
        const go = (url) => { if (!state.redirected) { state.redirected = true; window.location.assign(url || data.returnUrl); } };
        const stop = () => { state.redirected = true; }; // halt the poll without navigating (on error)
        if (data.orderId) {
          const started = Date.now();
          const poll = async () => {
            if (state.redirected || Date.now() - started > 120000) return;
            const r = await stripeConfirm(data.orderId);
            if (r.ok && r.data && r.data.paid) { go(r.data.redirect); return; }
            setTimeout(poll, 2500);
          };
          setTimeout(poll, 2500);
        }
        return { data, go, stop };
      };

      if (useProductRequest) {
        // ---- Android: Payment Request (Google Pay) ----
        const pr = stripe.paymentRequest({
          // Stripe wants the MERCHANT's country, which stays GR (the Stripe
          // account is Greek) even when the buyer is on the Cyprus storefront.
          country: 'GR',
          currency,
          total: { label: cfg.shopName || 'Adaptoras', amount },
          // Address/contact already collected in our checkout — don't ask the wallet.
          requestPayerName: false, requestPayerEmail: false, requestPayerPhone: false, requestShipping: false,
        });
        element = stripe.elements().create('paymentRequestButton', {
          paymentRequest: pr,
          style: { paymentRequestButton: { type: 'buy', theme: 'dark', height: '48px' } },
        });

        pr.canMakePayment().then((result) => {
          if (cancelled) return;
          if (result) {
            element.mount(nodeRef.current);
            setStatus('ready');
            diag({ event: 'pr_ready', available: Object.keys(result).filter((k) => result[k]).join(',') });
          } else {
            setStatus('unavailable');
            diag({ event: 'pr_unavailable' });
          }
        }).catch((e) => { if (!cancelled) { setStatus('unavailable'); diag({ event: 'pr_error', error: (e && e.message) || '' }); } });

        pr.on('paymentmethod', async (ev) => {
          setMsg('');
          const res = await beginServerPayment('google_pay');
          if (res.err) { ev.complete('fail'); setMsg(res.err); return; }
          const { data, go, stop } = res;
          const confirm = await stripe.confirmCardPayment(data.clientSecret, { payment_method: ev.paymentMethod.id }, { handleActions: false });
          if (confirm.error) { ev.complete('fail'); stop(); setMsg(confirm.error.message || 'Η πληρωμή δεν ολοκληρώθηκε.'); return; }
          ev.complete('success');
          if (confirm.paymentIntent && 'requires_action' === confirm.paymentIntent.status) {
            const after = await stripe.confirmCardPayment(data.clientSecret);
            if (after.error) { stop(); setMsg(after.error.message || 'Η επιβεβαίωση απέτυχε.'); return; }
          }
          go(data.returnUrl);
        });
      } else {
        // ---- iOS / desktop: Express Checkout Element (Apple Pay native + desktop QR) ----
        const elements = stripe.elements({ mode: 'payment', amount, currency });
        const paymentMethods = { applePay: 'always', googlePay: 'auto', link: 'never' };
        element = elements.create('expressCheckout', { paymentMethods });

        element.on('ready', (e) => {
          if (cancelled) return;
          const apm = e && e.availablePaymentMethods;
          setStatus(apm ? 'ready' : 'unavailable');
          diag({ event: 'ready', available: apm ? Object.keys(apm).filter((k) => apm[k]).join(',') : 'none' });
        });
        element.on('loaderror', (e) => {
          if (cancelled) return;
          diag({ event: 'loaderror', error: (e && e.error && e.error.message) || '' });
        });

        element.on('confirm', async (event) => {
          setMsg('');
          const { error: submitError } = await elements.submit();
          if (submitError) { setMsg(submitError.message || 'Σφάλμα επαλήθευσης.'); return; }
          const walletType = (event && event.expressPaymentType) || '';
          const res = await beginServerPayment(walletType);
          if (res.err) { setMsg(res.err); return; }
          const { data, go, stop } = res;
          // redirect:'if_required' → we drive the single navigation via go() so Stripe's
          // own redirect can't race the poll (which flashed the emptied cart).
          const { error, paymentIntent } = await stripe.confirmPayment({
            elements,
            clientSecret: data.clientSecret,
            confirmParams: { return_url: data.returnUrl },
            redirect: 'if_required',
          });
          if (error) { stop(); setMsg(error.message || 'Η πληρωμή δεν ολοκληρώθηκε.'); return; }
          if (paymentIntent && ('succeeded' === paymentIntent.status || 'processing' === paymentIntent.status)) {
            go(data.returnUrl);
          }
        });

        element.mount(nodeRef.current);
      }
    }).catch(() => { if (!cancelled) { setStatus('error'); setMsg('Δεν φόρτωσε το ' + label + '. Δοκίμασε ξανά.'); } });

    return () => {
      cancelled = true;
      if (element) { try { element.unmount(); } catch (e) { /* noop */ } try { element.destroy(); } catch (e) { /* noop */ } }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, currency]);

  return (
    <div className="cn-wallet">
      <div ref={nodeRef} className="cn-wallet__el" />
      {status === 'loading' && <p className="cn-review__muted">Φόρτωση {label}…</p>}
      {status === 'unavailable' && !msg && (
        <p className="cn-review__muted">Το {label} δεν είναι διαθέσιμο σε αυτόν τον browser. Διάλεξε άλλον τρόπο πληρωμής.</p>
      )}
      {msg && <div className="cn-errors" role="alert"><ul><li>{msg}</li></ul></div>}
    </div>
  );
}
